import { getEnv } from '@unni/platform'

export const getOS = (): DeviceOS | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const { userAgent } = window.navigator
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K']
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE']
  const iosPlatforms = ['iPhone', 'iPad', 'iPod']
  let os: ReturnType<typeof getOS> = null

  if (macosPlatforms.some((platform) => userAgent.includes(platform))) {
    os = 'Mac OS'
  } else if (iosPlatforms.some((platform) => userAgent.includes(platform))) {
    os = 'iOS'
  } else if (windowsPlatforms.some((platform) => userAgent.includes(platform))) {
    os = 'Windows'
  } else if (userAgent.includes('Android')) {
    os = 'Android'
  } else if (!os && userAgent.includes('Linux')) {
    os = 'Linux'
  }

  return os
}

export interface postMessageProperties {
  type?: string
  event?: string
  payload?: Record<string, string | number | string[] | number[] | object | boolean> | string | boolean
  data?: Record<string, string | number | string[] | number[] | object> | string | boolean
}

export const postOpenDeepLinkMessage = (url: string, withClose = true): void => {
  postMessage({
    type: 'openDeeplink',
    payload: {
      deeplink: `hbeauty://${url}`,
      withClose,
    },
  })
}

export const postMessage = (properties: postMessageProperties): void => {
  const stringifiedPayload = JSON.stringify(properties)

  const os = getOS()

  if (['test', 'local'].includes(`${getEnv('NEXT_PUBLIC_ENV')}`)) {
    console.log('postMessage payload:', properties)
  }

  if (os === 'iOS' && !!window.webkit) {
    window.webkit.messageHandlers.webView.postMessage(stringifiedPayload)
    return
  } else if (os === 'Android' && !!window && window.webView) {
    window.webView.postMessage(stringifiedPayload)
    return
  }
}

/**
 * User Agent 기반으로 WebView를 감지합니다.
 * 서버 사이드에서도 사용 가능합니다.
 */
export function detectWebViewFromUserAgent(userAgent: string | null | undefined): {
  isAndroid: boolean
  isiOS: boolean
  looksLikeAndroidWebView: boolean
  looksLikeIOSWebView: boolean
  looksLikeIOSSafari: boolean
  isInAppBrowserUA: boolean
  uaScore: number
  isWebView: boolean
} {
  if (!userAgent) {
    return {
      isAndroid: false,
      isiOS: false,
      looksLikeAndroidWebView: false,
      looksLikeIOSWebView: false,
      looksLikeIOSSafari: false,
      isInAppBrowserUA: false,
      uaScore: 0,
      isWebView: false,
    }
  }
  const ua = userAgent.toLowerCase()

  // A. UA 기반 파싱
  const isAndroid = ua.includes('android')
  const isiOS = /iphone|ipad|ipod/.test(ua)
  const hasAppleWebKit = ua.includes('applewebkit')
  const hasSafariToken = ua.includes('safari')
  const hasVersionToken = ua.includes('version/')
  const hasWV = ua.includes('; wv;')
  const hasFB = ua.includes('fban') || ua.includes('fbav')
  const hasInsta = ua.includes('instagram')
  const hasKakao = ua.includes('kakaotalk')
  const hasNaver = ua.includes('naver')
  const hasLine = ua.includes(' line/')
  const isGoogleApp = ua.includes(' gsa/')

  // iOS Safari의 전형: Version/x.y + Safari
  const looksLikeIOSSafari = isiOS && hasAppleWebKit && hasSafariToken && hasVersionToken

  // iOS WebView 흔한 패턴: AppleWebKit은 있으나 Safari 토큰 없음
  const looksLikeIOSWebView = isiOS && hasAppleWebKit && !hasSafariToken

  // Android WebView 흔한 패턴: ; wv; 또는 Version/4.0
  const looksLikeAndroidWebView = isAndroid && (hasWV || ua.includes('version/4.0'))

  // 인앱 브라우저 시그널(페북/인스타/카카오/네이버/라인/구글앱)은 "웹뷰 또는 인앱"으로 분류
  const isInAppBrowserUA = hasFB || hasInsta || hasKakao || hasNaver || hasLine || isGoogleApp

  // UA 기반 스코어 계산
  let uaScore = 0
  if (looksLikeAndroidWebView) uaScore += 2
  if (looksLikeIOSWebView && !looksLikeIOSSafari) uaScore += 2
  if (isInAppBrowserUA) uaScore += 1
  if (looksLikeIOSSafari) uaScore -= 2

  const isWebView = uaScore >= 2

  return {
    isAndroid,
    isiOS,
    looksLikeAndroidWebView,
    looksLikeIOSWebView,
    looksLikeIOSSafari,
    isInAppBrowserUA,
    uaScore,
    isWebView,
  }
}

export function detectWebView(win: Window | undefined): {
  isWebView: boolean
  signals: Record<string, boolean | string>
  score: number
} {
  try {
    const SCORE_THRESHOLD = 2
    if (!win) return { isWebView: false, signals: { noWindow: true }, score: 0 }

    const nav = win.navigator as any
    const ua: string = nav.userAgent || ''

    // A. UA 기반 감지
    const uaDetection = detectWebViewFromUserAgent(ua)

    // B. 브리지/객체 존재
    const hasRNBridge = typeof (win as any).ReactNativeWebView !== 'undefined'
    const hasWKMessageHandlers = !!(win as any).webkit?.messageHandlers
    const hasAndroidBridge = typeof (win as any).Android !== 'undefined'

    // C. PWA standalone (웹뷰 아님)
    const isStandaloneDisplayMode = win.matchMedia?.('(display-mode: standalone)').matches === true
    const isIOSStandalone = nav.standalone === true

    // 스코어링: 강한 신호(브리지) > 플랫폼별 전형적 UA > 인앱 UA
    let score = uaDetection.uaScore
    if (hasRNBridge || hasWKMessageHandlers || hasAndroidBridge) score += 3
    if (isStandaloneDisplayMode || isIOSStandalone) score -= 2

    const isWebView = score >= SCORE_THRESHOLD

    return {
      isWebView,
      signals: {
        ua: ua.toLowerCase(),
        isAndroid: uaDetection.isAndroid,
        isiOS: uaDetection.isiOS,
        looksLikeAndroidWebView: uaDetection.looksLikeAndroidWebView,
        looksLikeIOSWebView: uaDetection.looksLikeIOSWebView,
        looksLikeIOSSafari: uaDetection.looksLikeIOSSafari,
        hasRNBridge,
        hasWKMessageHandlers,
        hasAndroidBridge,
        isInAppBrowserUA: uaDetection.isInAppBrowserUA,
        isStandaloneDisplayMode,
        isIOSStandalone,
      },
      score,
    }
  } catch (error) {
    console.error('detectWebView error:', error)
    return { isWebView: false, signals: { noWindow: true }, score: 0 }
  }
}
